import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  type PushBlocker,
  type PushState,
  readPushState,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/push';
import { Icon } from './icons';

// Each blocker sends the user somewhere different, so they get their own wording.
// 'ios-not-installed' is the one that matters in practice: on iOS the push APIs are
// absent in a Safari tab, and a generic "not supported" would have the user hunting
// through browser settings for a switch that does not exist there.
const BLOCKER_TEXT: Record<PushBlocker, string> = {
  'ios-not-installed':
    'En iPhone y iPad las notificaciones solo funcionan si añades OWL a la pantalla de inicio: Compartir → Añadir a pantalla de inicio, y ábrela desde ahí.',
  unsupported: 'Este navegador no soporta notificaciones push.',
  denied:
    'Has bloqueado las notificaciones para OWL. Habilítalas en los ajustes del navegador para este sitio y vuelve a intentarlo.',
  'not-configured':
    'El servidor no tiene claves VAPID configuradas, así que el canal push está desactivado.',
};

export function PushToggle({ compact = false }: { compact?: boolean }) {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void readPushState().then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onEnable = useCallback(async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      // Called straight from the click handler: iOS rejects a permission request that
      // is not tied to a user gesture, and it does so for good.
      const next = await subscribeToPush();
      setState(next);
      if (next.subscribed) setMsg('Este dispositivo recibirá las alertas.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo activar');
    } finally {
      setBusy(false);
    }
  }, []);

  const onDisable = useCallback(async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await unsubscribeFromPush();
      setState(await readPushState());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo desactivar');
    } finally {
      setBusy(false);
    }
  }, []);

  const onTest = useCallback(async () => {
    if (!state?.endpoint) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.testPush(state.endpoint);
      setMsg('Notificación de prueba enviada.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'El envío de prueba falló');
    } finally {
      setBusy(false);
    }
  }, [state?.endpoint]);

  if (state === null) {
    return <div className="sub">Comprobando soporte de notificaciones…</div>;
  }

  const blockerText = state.blocker ? BLOCKER_TEXT[state.blocker] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.Bell size={14} />
            Notificaciones en este dispositivo
          </div>
          <div className="sub">
            {state.subscribed
              ? 'Activadas · llegan aunque OWL esté cerrada'
              : 'Desactivadas en este navegador'}
          </div>
        </div>
        {!state.blocker && (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={state.subscribed ? onDisable : onEnable}
          >
            {busy ? '…' : state.subscribed ? 'Desactivar' : 'Activar'}
          </button>
        )}
      </div>

      {state.subscribed && !compact && (
        <button type="button" className="btn btn-ghost" disabled={busy} onClick={onTest}>
          Enviar notificación de prueba
        </button>
      )}

      {blockerText && (
        <div className="sub" style={{ color: 'var(--fg-3)', lineHeight: 1.45 }}>
          {blockerText}
        </div>
      )}
      {msg && (
        <div className="sub" style={{ color: '#92d3a3' }}>
          {msg}
        </div>
      )}
      {err && (
        <div className="sub" style={{ color: '#e0736f' }}>
          {err}
        </div>
      )}
    </div>
  );
}
